import { useState } from 'react'
import { Group, Loader, Modal, Pagination, Table, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'

import { useConnectionAccounts } from '../hooks/useAdminConnections'
import type { RosterConnectionInfo } from '../types'

interface Props {
  connection: RosterConnectionInfo | null
  onClose: () => void
}

/**
 * Read-only list of the accounts this connection's sync created — one account
 * per school, with the staff imported as its managers.
 */
const AccountsModal = ({ connection, onClose }: Props) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const { accounts, pagination, isLoading, isError } = useConnectionAccounts(
    connection?.id ?? null,
    page,
  )

  const handleClose = () => {
    setPage(1)
    onClose()
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <Group position='center' py='xl'>
          <Loader />
        </Group>
      )
    }

    if (isError) {
      return (
        <Text role='alert' color='red'>
          {t('admin_connections.load_error')}
        </Text>
      )
    }

    if (accounts.length === 0) {
      return <Text color='dimmed'>{t('admin_connections.no_accounts')}</Text>
    }

    return (
      <Table striped verticalSpacing='sm'>
        <thead>
          <tr>
            <th>{t('admin_connections.account')}</th>
            <th>{t('admin_connections.schools')}</th>
            <th>{t('admin_connections.managers')}</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map(account => (
            <tr key={account.id}>
              <td>
                <Text weight={500}>{account.name}</Text>
              </td>
              <td>
                <Text size='sm'>
                  {account.schools.map(school => school.name).join(', ') || '—'}
                </Text>
              </td>
              <td>
                <Text size='sm' color='dimmed' lineClamp={1}>
                  {account.managers.map(manager => manager.email).join(', ') || '—'}
                </Text>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  return (
    <Modal
      opened={connection !== null}
      onClose={handleClose}
      size='lg'
      title={t('admin_connections.accounts_modal_title', {
        district: connection?.districtName || connection?.districtExternalId || '',
      })}
    >
      {renderBody()}
      {pagination && pagination.pages > 1 && (
        <Group position='center' mt='md'>
          <Pagination value={page} onChange={setPage} total={pagination.pages} />
        </Group>
      )}
    </Modal>
  )
}

export default AccountsModal
